export type FeatureAggregationRule = "sum" | "override";

export interface FeatureKeyDefinition {
  key: string;
  type: "boolean" | "number" | "string";
  defaultValue: boolean | number | string;
  aggregation: FeatureAggregationRule;
  description: string;
  /** Optional fixed choices for string-typed keys (renders as a select). */
  options?: readonly string[];
}

export const FEATURE_KEYS = {
  APPOINTMENTS_PER_MONTH: {
    key: "appointmentsPerMonth",
    type: "number" as const,
    defaultValue: 0,
    aggregation: "sum" as const,
    description: "Maximum appointments per month (0 = unlimited)",
  },
  ONLINE_BOOKING: {
    key: "onlineBooking",
    type: "boolean" as const,
    defaultValue: false,
    aggregation: "override" as const,
    description: "Online booking enabled",
  },
  SMS_CAMPAIGNS: {
    key: "smsCampaigns",
    type: "boolean" as const,
    defaultValue: false,
    aggregation: "override" as const,
    description: "SMS campaign sending enabled",
  },
  SMS_CREDITS: {
    key: "smsCredits",
    type: "number" as const,
    defaultValue: 0,
    aggregation: "sum" as const,
    description: "Monthly SMS credits (0 = none)",
  },
  WHITE_LABEL: {
    key: "whiteLabel",
    type: "boolean" as const,
    defaultValue: false,
    aggregation: "override" as const,
    description: "White-label branding (remove platform branding)",
  },
  MAX_WORKERS: {
    key: "maxWorkers",
    type: "number" as const,
    defaultValue: 5,
    aggregation: "sum" as const,
    description: "Maximum number of workers",
  },
  MAX_SALONS: {
    key: "maxSalons",
    type: "number" as const,
    defaultValue: 1,
    aggregation: "sum" as const,
    description: "Maximum number of salons under the franchise",
  },
  SUPPORT_TIER: {
    key: "supportTier",
    type: "string" as const,
    defaultValue: "basic",
    aggregation: "override" as const,
    description: "Support tier",
    options: ["basic", "priority", "dedicated"] as const,
  },
  ADVANCED_ANALYTICS: {
    key: "advancedAnalytics",
    type: "boolean" as const,
    defaultValue: false,
    aggregation: "override" as const,
    description: "Advanced analytics and reporting dashboard",
  },
  LOYALTY_PROGRAM: {
    key: "loyaltyProgram",
    type: "boolean" as const,
    defaultValue: false,
    aggregation: "override" as const,
    description: "Loyalty points program",
  },
  AI_ASSISTANT: {
    key: "aiAssistant",
    type: "boolean" as const,
    defaultValue: false,
    aggregation: "override" as const,
    description: "AI-powered assistant and recommendations",
  },
  AI_CREDITS: {
    key: "aiCredits",
    type: "number" as const,
    defaultValue: 0,
    aggregation: "sum" as const,
    description: "Monthly AI credits (0 = none) — adds across plan + AI add-ons",
  },
} as const satisfies Record<string, FeatureKeyDefinition>;

export type FeatureKeyName = keyof typeof FEATURE_KEYS;
export type FeatureKey = (typeof FEATURE_KEYS)[FeatureKeyName]["key"];

export type EffectiveFeatures = Partial<{
  appointmentsPerMonth: number;
  onlineBooking: boolean;
  smsCampaigns: boolean;
  smsCredits: number;
  whiteLabel: boolean;
  maxWorkers: number;
  maxSalons: number;
  supportTier: string;
  advancedAnalytics: boolean;
  loyaltyProgram: boolean;
  aiAssistant: boolean;
  aiCredits: number;
}>;

export const FEATURE_KEY_DEFAULTS: EffectiveFeatures = Object.fromEntries(
  Object.values(FEATURE_KEYS).map((def) => [def.key, def.defaultValue])
) as EffectiveFeatures;

export const FEATURE_KEY_LIST: readonly FeatureKeyDefinition[] = Object.values(FEATURE_KEYS);
